import { useState } from 'react';
import { Button } from '../Button';
import { showErrorToast, showSuccessToast } from '../../lib/ToastUtility';
import { getApiErrorMessage } from '../../utils/apiError';
import { sendKioskRegistrationProposal } from '../../services/KioskRegistrationService';
import type {
  KioskRegistration,
  KioskRegistrationChannelType,
} from '../../types/kioskRegistration.types';
import ModalShell from './ModalShell';
import { CHANNEL_TYPE_LABELS, INPUT_CLASS } from './registrationUi';

interface Props {
  registration: KioskRegistration;
  onClose: () => void;
  onDone: () => void;
}

const MAX_FEE_CENTS = 100_000_000;
const MONEY_PATTERN = /^\d+(\.\d{1,2})?$/;

function centsToDollars(cents: number | null): string {
  return cents === null || cents === undefined ? '' : (cents / 100).toFixed(2);
}

function dollarsToCents(value: string): number | null {
  const trimmed = value.trim();
  if (!MONEY_PATTERN.test(trimmed)) return null;
  const cents = Math.round(parseFloat(trimmed) * 100);
  return cents >= 0 && cents <= MAX_FEE_CENTS ? cents : null;
}

export default function SendProposalModal({ registration, onClose, onDone }: Props) {
  const [channelType, setChannelType] = useState<KioskRegistrationChannelType | ''>(
    registration.channelType ?? '',
  );
  const [onboardingFee, setOnboardingFee] = useState(centsToDollars(registration.onboardingFeeCents));
  const [monthlyFee, setMonthlyFee] = useState(centsToDollars(registration.monthlyFeeCents));
  const [termsDocumentUrl, setTermsDocumentUrl] = useState(registration.termsDocumentUrl ?? '');
  const [personalNote, setPersonalNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isResend = Boolean(registration.proposalSentAt);

  const submit = async () => {
    const onboardingFeeCents = dollarsToCents(onboardingFee);
    const monthlyFeeCents = dollarsToCents(monthlyFee);
    const url = termsDocumentUrl.trim();

    if (!channelType) return setError('Select the channel type agreed on the call.');
    if (onboardingFeeCents === null) return setError('Enter a valid onboarding fee in dollars (e.g. 250.00, or 0).');
    if (monthlyFeeCents === null) return setError('Enter a valid monthly fee in dollars (e.g. 49.00, or 0).');
    if (!/^https:\/\/\S+$/i.test(url)) return setError('The terms document link must be an https PandaDoc URL.');

    setSubmitting(true);
    setError(null);
    try {
      await sendKioskRegistrationProposal(registration.id, {
        channelType,
        onboardingFeeCents,
        monthlyFeeCents,
        currencyCode: 'USD',
        termsDocumentUrl: url,
        personalNote: personalNote.trim() || undefined,
      });
      showSuccessToast(`Proposal email sent to ${registration.email}.`);
      onDone();
    } catch (requestError) {
      const message = getApiErrorMessage(requestError, 'The proposal email could not be sent.');
      setError(message);
      showErrorToast(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title={`${isResend ? 'Resend' : 'Send'} proposal — ${registration.referenceCode}`}
      size="lg"
      onClose={submitting ? () => undefined : onClose}
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose} disabled={submitting}>
            Close
          </Button>
          <Button size="md" onClick={() => void submit()} loading={submitting}>
            {isResend ? 'Resend email' : 'Send email'}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm text-gray-700 dark:text-gray-200">
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
          This e-mails <strong>{registration.email}</strong> using the “Tap2Feed: Joining the network”
          template (Thank you for your interest) with the channel summary below and the Terms &amp;
          Conditions link.
        </p>

        <label className="block font-medium">
          Channel type
          <select
            value={channelType}
            onChange={(event) => setChannelType(event.target.value as KioskRegistrationChannelType | '')}
            className={`mt-1.5 ${INPUT_CLASS}`}
          >
            <option value="">Select…</option>
            {Object.entries(CHANNEL_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>

        <div className="grid gap-4 sm:grid-cols-3">
          <label className="block font-medium">
            Onboarding fee ($)
            <input
              inputMode="decimal"
              value={onboardingFee}
              onChange={(event) => setOnboardingFee(event.target.value)}
              placeholder="0.00"
              className={`mt-1.5 ${INPUT_CLASS}`}
            />
          </label>
          <label className="block font-medium">
            Monthly fee ($)
            <input
              inputMode="decimal"
              value={monthlyFee}
              onChange={(event) => setMonthlyFee(event.target.value)}
              placeholder="0.00"
              className={`mt-1.5 ${INPUT_CLASS}`}
            />
          </label>
          <label className="block font-medium">
            Currency
            <input
              value="USD"
              readOnly
              aria-describedby="proposal-currency-help"
              className={`mt-1.5 cursor-not-allowed opacity-70 ${INPUT_CLASS}`}
            />
          </label>
        </div>
        <p id="proposal-currency-help" className="-mt-2 text-xs text-gray-500 dark:text-gray-400">
          Fees are entered in dollars; 0 is allowed. The billing system issues USD invoices.
        </p>

        <label className="block font-medium">
          Terms &amp; Conditions document (PandaDoc link)
          <input
            type="url"
            value={termsDocumentUrl}
            onChange={(event) => setTermsDocumentUrl(event.target.value)}
            maxLength={1000}
            placeholder="https://app.pandadoc.com/…"
            className={`mt-1.5 ${INPUT_CLASS}`}
          />
        </label>

        <label className="block font-medium">
          Personal note (optional)
          <textarea
            value={personalNote}
            onChange={(event) => setPersonalNote(event.target.value)}
            maxLength={1000}
            rows={3}
            className={`mt-1.5 ${INPUT_CLASS}`}
          />
        </label>

        {error && (
          <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
